import React from 'react';
import { Link } from 'react-router-dom';
import { User } from '../../types/user'; // Correct: Import User type
// Import connection types and Button
import { ConnectionStatus } from '../../services/userService'; 
import { Button, Spinner } from 'react-bootstrap';
// Import FontAwesomeIcon and relevant icons
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserPlus, faCheck, faHourglassHalf, faUserClock, faLink, faPaperPlane } from '@fortawesome/free-solid-svg-icons';

interface ProfileHeaderProps {
    user: User | any; // Allow broader type for now to fix immediate error, refine later
    isOwnProfile: boolean | null;
    // Add connection status props
    connectionStatus?: ConnectionStatus;
    isLoadingConnectionStatus?: boolean;
    onConnectClick?: () => void;
    onMessageClick?: () => void;
    // Add props for data not directly on User object if needed later (e.g., separate interests fetch?)
}

// Placeholder icons (replace with FontAwesome or similar later)
const icons = {
    linkedin: 'LI', // Placeholder text
    edit: '✏️', // Emoji
    connections: '👥' // Emoji
};

const ProfileHeader: React.FC<ProfileHeaderProps> = ({ 
    user, 
    isOwnProfile, 
    connectionStatus, 
    isLoadingConnectionStatus,
    onConnectClick,
    onMessageClick
}) => {
    // Function to get initials from name
    const getInitials = (name: string | undefined): string => {
        if (!name) return '?';
        const names = name.split(' ');
        // Handle potential single names
        if (names.length === 1) return names[0][0].toUpperCase(); 
        return (names[0][0] + names[names.length - 1][0]).toUpperCase();
    };

    // Construct location name if needed
    const locationName = user.city 
        ? `${user.city}${user.country ? `, ${user.country}` : ''}` 
        : undefined;

    // --- Helper to render Connection Button --- //
    const renderConnectButton = () => {
        if (isOwnProfile) return null; // Don't show on own profile
        
        if (isLoadingConnectionStatus) {
            return (
                <Button variant="outline-secondary" className="rounded-pill mb-2 d-block d-md-inline-block" disabled>
                    <Spinner animation="border" size="sm" role="status" aria-hidden="true" className="me-1" /> Loading...
                </Button>
            );
        }

        switch (connectionStatus) {
            case 'connected':
                return (
                    <Button variant="outline-success" className="rounded-pill mb-2 d-block d-md-inline-block" disabled>
                        <FontAwesomeIcon icon={faCheck} className="me-1" /> Connected
                    </Button>
                );
            case 'pending_sent':
                return (
                    <Button variant="outline-secondary" className="rounded-pill mb-2 d-block d-md-inline-block" disabled>
                        <FontAwesomeIcon icon={faHourglassHalf} className="me-1" /> Request Sent
                    </Button>
                );
            case 'pending_received':
                return (
                    <Button variant="outline-info" className="rounded-pill mb-2 d-block d-md-inline-block" disabled>
                        <FontAwesomeIcon icon={faUserClock} className="me-1" /> Request Received
                    </Button>
                    // Or maybe link to connections page: <Link to="/connections" ...>
                );
            case 'not_connected':
                return (
                    <Button variant="success" className="rounded-pill mb-2 d-block d-md-inline-block" onClick={onConnectClick}>
                        <FontAwesomeIcon icon={faLink} className="me-1" /> Connect
                    </Button>
                );
            default:
                // If status is undefined (e.g., error fetching or logged out), don't show button
                return null; 
        }
    };

    return (
        // Card structure from original template
        <div className="card border-0 shadow-sm mb-4"> 
            <div className="card-body p-4">
                <div className="row align-items-center">
                    {/* Profile Initial Circle */}
                    <div className="col-md-2 col-sm-3 mb-3 mb-md-0 text-center">
                        <div 
                            className="rounded-circle bg-primary text-white d-inline-flex align-items-center justify-content-center mb-3"
                            style={{ width: '100px', height: '100px' }}
                        >
                            <span className="fw-bold display-6">{getInitials(user.name)}</span>
                        </div>
                    </div>

                    {/* User Info & Links */}
                    <div className="col-md-7 col-sm-9">
                        <h1 className="h2 fw-bold mb-1">{user.name}</h1>
                        <p className="text-muted mb-2">
                            {user.profession}
                            {locationName && ` • ${locationName}`}
                        </p>
                        {user.bio && (
                            <p className="mb-3">{user.bio}</p>
                        )}

                        {/* Social/Professional Links */}
                        <div className="d-flex gap-3 mb-3">
                            {user.linkedin ? (
                                <a href={user.linkedin} target="_blank" rel="noopener noreferrer" className="text-decoration-none" title="LinkedIn Profile">
                                    <div className="d-flex align-items-center">
                                        <div className="rounded-circle bg-light p-2">
                                            {/* TODO: Replace with actual icon component */}
                                            <span className="text-primary">{icons.linkedin}</span>
                                        </div>
                                    </div>
                                </a>
                            ) : (
                                /* Placeholder for adding link if not present */
                                <a href="#" className="text-decoration-none text-muted" title="Add LinkedIn Profile" onClick={(e) => e.preventDefault()}>
                                    <div className="d-flex align-items-center">
                                        <div className="rounded-circle bg-light p-2">
                                            {/* TODO: Replace with actual icon component */}
                                            <span>{icons.linkedin}</span>
                                        </div>
                                    </div>
                                </a>
                            )}
                            {/* GitHub/Website links removed as per original template */}
                        </div>

                        {/* Interests */}
                        <div className="d-flex flex-wrap gap-2">
                            {(user.skills && user.skills.length > 0) ? (
                                user.skills.map((skill: string, index: number) => (
                                    <span key={index} className="badge bg-light text-dark border">{skill}</span>
                                ))
                            ) : (
                                <span className="text-muted small">No interests/skills listed.</span>
                            )}
                        </div>
                    </div>

                    {/* Edit Button & Connections */}
                    <div className="col-md-3 mt-3 mt-md-0 text-md-end">
                         {/* Conditionally render Edit button */}
                         {isOwnProfile && (
                            <Link to="/profile/edit" className="btn btn-outline-primary rounded-pill mb-2 d-block d-md-inline-block">
                                {/* TODO: Replace with actual icon component */}
                                 <span className="me-2">{icons.edit}</span> Edit Profile
                            </Link>
                         )}
                         {/* Render Connect Button (or status) */}
                         {!isOwnProfile && renderConnectButton()} 

                         {/* Message Button - Add here */}
                         {!isOwnProfile && onMessageClick && (
                            <Button
                                variant="info" 
                                className="rounded-pill mb-2 d-block d-md-inline-block ms-md-2 mt-2 mt-md-0" 
                                onClick={onMessageClick}
                            >
                                <FontAwesomeIcon icon={faPaperPlane} className="me-1" /> Message
                            </Button>
                         )}

                        {/* Connections - Data might be missing from User type */}
                        {/* <div className="text-muted small mt-2">
                            <span className="me-1">{icons.connections}</span> {user.connectionsCount ?? 0} connections
                        </div> */}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProfileHeader; 